import { z } from 'zod';
import { MASKENFORMEN } from './maske.js';
/**
 * Timeline model.
 *
 * A project owns exactly one sequence. The sequence has tracks; track index 0 of
 * kind "video" is the *base* track: its clips are laid end to end and may be
 * joined by transitions. Higher video tracks are *overlay* tracks -- their clips
 * are composited on top of whatever the base track shows at that moment. Audio
 * tracks are always mixed.
 *
 * All times are seconds on the sequence timeline unless the field name says
 * otherwise (`sourceIn` / `sourceOut` are seconds inside the source asset).
 */
export const ID = z.string().min(1).max(64);
export const Rect = z.object({
    /** Fraction of canvas width, 0..1, of the layer centre. */
    x: z.number().default(0.5),
    /** Fraction of canvas height, 0..1, of the layer centre. */
    y: z.number().default(0.5),
    /** Uniform scale factor applied to the layer. */
    scale: z.number().positive().default(1),
    rotate: z.number().default(0),
    opacity: z.number().min(0).max(1).default(1),
});
/**
 * Wie eine Bewegung von einem Keyframe zum nächsten verläuft (S30). Sascha:
 * „Ein- und Ausschwingen statt gleichförmig.“
 *
 * Die Liste steht hier, weil das Schema sie braucht; gerechnet wird in
 * `animation/keyframes.ts` -- einmal für die Vorschau und einmal als Ausdruck
 * für ffmpeg, aus derselben Stelle.
 */
export const KURVEN = ['weich', 'linear', 'rein', 'raus'];
export const KURVEN_NAMEN = {
    weich: 'Weich',
    linear: 'Gleichförmig',
    rein: 'Langsam los',
    raus: 'Langsam an',
};
export const Keyframe = z.object({
    /** Seconds into the clip. */
    at: z.number().min(0),
    transform: Rect,
    /**
     * Wie die Strecke **von hier zum nächsten** verläuft (S30).
     *
     * Voreingestellt „weich“ -- das ist, was bis zum 17. September das Einzige
     * war. Ein Schnitt von gestern sieht damit morgen genauso aus.
     */
    kurve: z.enum(KURVEN).default('weich'),
});
/**
 * Ein Pegelpunkt: hier ist der Clip so laut.
 *
 * Eigene Punkte, nicht das Feld `gain` im Keyframe oben: Bild und Ton werden
 * an verschiedenen Stellen angefasst. Wer eine Bauchbinde an Sekunde 2
 * verschiebt, will dort nicht auch die Lautstärke festnageln — und ein
 * Musikbett hat gar kein Bild, an dem eine Bewegung hinge.
 */
export const GainKeyframe = z.object({
    /** Sekunden in den Clip hinein. */
    at: z.number().min(0),
    /** Linearer Pegel an dieser Stelle; 1 ist unverändert. */
    gain: z.number().min(0).max(4),
});
export const ColorAdjust = z.object({
    type: z.literal('colorAdjust'),
    brightness: z.number().min(-1).max(1).default(0),
    contrast: z.number().min(0).max(4).default(1),
    saturation: z.number().min(0).max(3).default(1),
    gamma: z.number().min(0.1).max(4).default(1),
});
export const LutEffect = z.object({
    type: z.literal('lut'),
    /** Name of a .cube file shipped in the LUT directory, e.g. "teal-orange". */
    name: z.string().min(1).max(64),
    intensity: z.number().min(0).max(1).default(1),
});
export const BlurEffect = z.object({
    type: z.literal('blur'),
    amount: z.number().min(0).max(50).default(6),
});
export const VignetteEffect = z.object({
    type: z.literal('vignette'),
    amount: z.number().min(0).max(1).default(0.4),
});
export const SharpenEffect = z.object({
    type: z.literal('sharpen'),
    amount: z.number().min(0).max(3).default(1),
});
/**
 * Greenscreen (S11). Sascha: „Eine Farbe herausschneiden und etwas dahinter
 * setzen.“
 *
 * `toleranz` sagt, wie weit eine Farbe von der gewählten abweichen darf und
 * trotzdem verschwindet; `weich` macht die Kante weich, damit ein Haar nicht
 * wie mit der Schere ausgeschnitten aussieht. Beides sind die beiden Zahlen,
 * die `chromakey` in ffmpeg kennt -- mehr Regler wären mehr, als man am Telefon
 * einstellen mag, und weniger reicht nicht: ohne Toleranz bleibt jeder
 * Schatten auf dem Tuch stehen.
 */
export const ChromaEffect = z.object({
    type: z.literal('chroma'),
    /** Die Farbe, die verschwindet. Grün, weil Haut am wenigsten davon hat. */
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#00b140'),
    toleranz: z.number().min(0.01).max(0.9).default(0.2),
    weich: z.number().min(0).max(0.5).default(0.05),
});
/**
 * Bildstabilisierung (S12). Sascha: „Verwackelte Handaufnahmen ruhig rechnen.“
 *
 * Eine Zahl, kein Dutzend: wie stark geglättet wird. Dahinter stecken zwei
 * Durchgänge von ffmpeg -- erst wird die Bewegung vermessen, dann
 * ausgeglichen --, und beide bekommen ihre Werte aus dieser einen.
 */
export const StabiEffect = z.object({
    type: z.literal('stabi'),
    staerke: z.number().min(0.1).max(1).default(0.5),
});
/**
 * Farbräder (S14). Sascha: „Drei Bereiche (Tiefen, Mitten, Höhen) statt eines
 * Reglers, und ein Klick auf etwas Graues richtet die Farben aus.“
 *
 * Je Bereich drei Zahlen von -1 bis 1, eine für jede Farbe -- genau das, was
 * `colorbalance` in ffmpeg kennt. Ein Rad auf dem Bildschirm wäre hübscher und
 * mit dem Daumen nicht zu treffen; zwei Schieber je Bereich (wärmer/kühler und
 * grün/magenta) rechnen auf dieselben drei Zahlen.
 */
const Dreiklang = z.tuple([z.number().min(-1).max(1), z.number().min(-1).max(1), z.number().min(-1).max(1)]);
export const FarbraederEffect = z.object({
    type: z.literal('farbraeder'),
    /** Rot, Grün, Blau in den Tiefen. */
    tiefen: Dreiklang.default([0, 0, 0]),
    mitten: Dreiklang.default([0, 0, 0]),
    hoehen: Dreiklang.default([0, 0, 0]),
});
/**
 * Werkzeuge für den Ton (S19). Sascha: „Rauschminderung, Kompressor,
 * Equalizer, Hall -- das, was eine Stimme sendefähig macht.“
 *
 * Eine eigene Liste neben `effects` und nicht darin: die Bildeffekte gehen
 * durch `cssFilterVon` und die Leinwand, die Toneffekte durch die Tonkette.
 * Beides in einen Topf zu werfen hiesse, an jeder Stelle erst zu fragen,
 * womit man es zu tun hat.
 */
export const Entrauschen = z.object({
    type: z.literal('entrauschen'),
    /** 0 = aus, 1 = so viel, wie `afftdn` hergibt (24 dB). */
    staerke: z.number().min(0).max(1).default(0.5),
});
export const Kompressor = z.object({
    type: z.literal('kompressor'),
    /** 0 = sanft (2:1), 1 = fest (8:1) -- samt Schwelle und Ausgleich. */
    staerke: z.number().min(0).max(1).default(0.5),
});
export const Equalizer = z.object({
    type: z.literal('equalizer'),
    /** Anhebung oder Absenkung in Dezibel, je Bereich. */
    tiefen: z.number().min(-12).max(12).default(0),
    mitten: z.number().min(-12).max(12).default(0),
    hoehen: z.number().min(-12).max(12).default(0),
});
export const TonEffekt = z.discriminatedUnion('type', [Entrauschen, Kompressor, Equalizer]);
export const Effect = z.discriminatedUnion('type', [
    ColorAdjust,
    LutEffect,
    BlurEffect,
    VignetteEffect,
    SharpenEffect,
    ChromaEffect,
    StabiEffect,
    FarbraederEffect,
]);
export const TRANSITIONS = [
    'crossfade',
    'dipToBlack',
    'dipToWhite',
    'wipeleft',
    'wiperight',
    'wipeup',
    'wipedown',
    'slideleft',
    'slideright',
    'circleopen',
    // Wischblenden (S9): „Balken, Uhr, Iris, Diagonale“. Uhr ist `radial`, die
    // Iris auf ist `circleopen` -- beide gab es schon. Dazu kommen die Iris zu,
    // die beiden Balken und die zwei Diagonalen. Die Namen sind die von ffmpeg,
    // damit im Export nichts übersetzt werden muss, was man auch verwechseln
    // kann; wie sie *aussehen*, steht gemessen in `scripts/blende-smoke.mjs`.
    'circleclose',
    'horzopen',
    'vertopen',
    'diagbr',
    'diagtl',
    'radial',
    'pixelize',
];
export const TransitionKind = z.enum(TRANSITIONS);
export const Transition = z.object({
    kind: TransitionKind.default('crossfade'),
    /** Seconds. Consumes tail of the previous clip and head of this one. */
    duration: z.number().min(0.04).max(5).default(0.5),
});
export const TextStyle = z.object({
    fontFamily: z.string().max(80).default('Inter'),
    /** Fraction of canvas height. 0.06 ~= 65px on 1080p. */
    size: z.number().min(0.01).max(0.4).default(0.06),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#ffffff'),
    boxColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().default(null),
    boxOpacity: z.number().min(0).max(1).default(0.6),
    /** Outline colour. Null means no outline. */
    outlineColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().default(null),
    /** Outline thickness as a fraction of the font size, so it scales with it. */
    outlineWidth: z.number().min(0).max(0.3).default(0.08),
    align: z.enum(['left', 'center', 'right']).default('center'),
    shadow: z.boolean().default(true),
    bold: z.boolean().default(false),
    uppercase: z.boolean().default(false),
});
/** What a clip actually shows. */
export const ClipSource = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('asset'), assetId: ID }),
    z.object({ kind: z.literal('text'), text: z.string().max(2000), style: TextStyle }),
    z.object({
        kind: z.literal('color'),
        color: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#000000'),
    }),
    z.object({
        kind: z.literal('brand'),
        /** Id of a template inside the project's brand kit. */
        templateId: ID,
        /** Values for the template's named slots, e.g. { name: "Ada", role: "CTO" }. */
        fields: z.record(z.string().max(400)).default({}),
    }),
]);
export const Maske = z.object({
    form: z.enum(MASKENFORMEN),
    /**
     * Weiche Kante, als Anteil der Ebene. 0 ist eine harte Kante -- und das ist
     * die Vorgabe, weil Vorschau und Export dort nachweislich Punkt für Punkt
     * dasselbe ergeben (7.82). Eine weiche Kante rechnet der Browser mit einer
     * Unschärfe nach und der Export mit einer Geraden; das ist nah beieinander,
     * aber nicht dasselbe, und darum wählt es niemand aus Versehen.
     */
    weich: z.number().min(0).max(0.5).default(0),
    /**
     * Wo die Form in der Ebene sitzt, wie gross sie ist und wie sie steht
     * (7.82b). Bis hierher war sie in die Ebene eingespannt: eine
     * Gedankenblase sass mittig und füllte das Rechteck aus, und wer sie
     * woanders haben wollte, musste die ganze Ebene verschieben -- und
     * verschob damit auch, welcher Ausschnitt des Videos zu sehen ist.
     *
     * `x`/`y` ist die Mitte der Form in Anteilen der Ebene, und sie darf über
     * den Rand hinaus: eine Blase, die halb aus dem Bild ragt, ist ein
     * gewolltes Bild. `groesse` ist der Faktor auf die eingespannte Form,
     * `drehung` der Winkel im Uhrzeigersinn.
     *
     * Gedreht wird in **Bildpunkten und nicht in Anteilen**. In Anteilen
     * gerechnet würde ein Kreis auf einer breiten Ebene beim Drehen zur
     * schiefen Ellipse -- die Vorgabe 0 hätte das nie gezeigt.
     */
    x: z.number().min(-1).max(2).default(0.5),
    y: z.number().min(-1).max(2).default(0.5),
    groesse: z.number().min(0.05).max(4).default(1),
    drehung: z.number().min(-180).max(180).default(0),
    /**
     * **Die Kontur** (7.224; Sascha: „Mehr wie ein Comic sollte sie
     * aussehen“). Ein Strich in `farbe` **innen** am Rand der Form, `staerke`
     * in denselben Anteilen der Ebene wie `weich`. Innen und nicht aussen: die
     * Form füllt die Ebene oft ganz, und ein Strich aussen wäre am Rand der
     * Ebene abgeschnitten. Fehlt sie, gibt es keinen Strich -- wie bisher.
     */
    kontur: z
        .object({ farbe: z.string().regex(/^#[0-9a-fA-F]{6}$/), staerke: z.number().min(0).max(0.1) })
        .nullable()
        .default(null),
    /**
     * **Bläschen und Spitze rechts statt links** (7.224, Teil 4: „Richtung
     * der Bläschen“). Gespiegelt wird im Feld der Form, also vor Lage und
     * Drehung -- die Blase bleibt, wo sie sitzt, nur ihr Schwanz zeigt zur
     * anderen Seite. Fehlt es, zeigt er nach links wie bisher.
     */
    gespiegelt: z.boolean().optional(),
});
export const Clip = z.object({
    id: ID,
    source: ClipSource,
    /** Start on the sequence timeline, seconds. */
    start: z.number().min(0),
    /** Visible length on the sequence timeline, seconds, after speed is applied. */
    duration: z.number().positive(),
    /** Seconds into the source asset. Ignored for generated sources. */
    sourceIn: z.number().min(0).default(0),
    /** Playback rate. 2 = twice as fast. Mit einer Rampe (S6) ist das der Wert
     *  am **Anfang** des Clips. */
    speed: z.number().min(0.25).max(8).default(1),
    /**
     * Das Tempo am **Ende** des Clips (S6) -- `null` heisst: keine Rampe, es
     * bleibt bei `speed`.
     *
     * Sascha: „Langsamer werden statt umschalten, und die Tonhöhe bleibt, wo sie
     * war.“ Zwei Zahlen statt einer Kurve: eine Gerade kann man im Kopf
     * ausrechnen, und der Schnitt dahinter rutscht nicht.
     */
    speedBis: z.number().min(0.25).max(8).nullable().default(null),
    /** Linear gain for this clip's audio. */
    gain: z.number().min(0).max(4).default(1),
    muted: z.boolean().default(false),
    transform: Rect.default({}),
    /** Position, size and rotation at chosen moments of the clip; see animation/keyframes.ts. */
    keyframes: z.array(Keyframe).max(32).default([]),
    /** Der Pegelverlauf über den Clip; leer heißt: überall `gain`. */
    gainKeyframes: z.array(GainKeyframe).max(64).default([]),
    effects: z.array(Effect).max(8).default([]),
    /** Werkzeuge für den Ton (S19) -- höchstens eines je Art. */
    tonEffekte: z.array(TonEffekt).max(3).default([]),
    fadeIn: z.number().min(0).max(10).default(0),
    fadeOut: z.number().min(0).max(10).default(0),
    /** Transition to the clip that precedes it. Base video track only. */
    transitionIn: Transition.nullable().default(null),
    /**
     * How the layer arrives. Movement only -- fading is `fadeIn`/`fadeOut`, and
     * the two combine. Ignored on the base track, which has nowhere to slide
     * from.
     */
    animation: z.enum(['none', 'slideLeft', 'slideRight', 'slideUp', 'slideDown']).default('none'),
    /**
     * Die Form, in der diese Ebene läuft (7.82) -- Kreis, Ellipse, runde Ecken
     * oder Gedankenblase. `null` heisst: das ganze Rechteck, wie bisher.
     *
     * Nur für Ebenen: die Basisspur *ist* das Bild, eine Form darin liesse
     * schwarz darum herum stehen.
     */
    maske: Maske.nullable().default(null),
    label: z.string().max(120).default(''),
    /**
     * Die Gruppe, zu der dieser Clip gehört (S3). `null` heisst: keine.
     *
     * Sascha: „Bild und Ton, die zusammengehören, bewegen sich zusammen -- bis
     * man die Gruppe wieder löst.“ Eine Kennung am Clip und nicht eine Liste
     * neben dem Schnitt: so kann ein Clip nicht in zwei Gruppen stehen, und
     * wird er gelöscht, ist er auch aus der Gruppe -- ohne dass irgendwer
     * aufräumen muss.
     */
    gruppe: ID.nullable().default(null),
});
/**
 * Wie eine Bildspur auf der darunter liegt (S10). Sascha: „Multiplizieren,
 * Aufhellen, Weiches Licht.“
 *
 * Die Namen sind die von ffmpeg (`blend=all_mode=...`), damit im Export nichts
 * übersetzt werden muss, was man auch verwechseln kann -- dieselbe
 * Entscheidung wie bei den Wischblenden (S9). Im Browser heissen dieselben drei
 * in CSS `multiply`, `screen` und `soft-light`; die Zuordnung steht an einer
 * Stelle und wird geprüft.
 */
export const MISCHMODI = ['normal', 'multiply', 'screen', 'softlight'];
export const Mischmodus = z.enum(MISCHMODI);
/** Wie die Vorschau im Browser dieselben drei nennt (CSS `mix-blend-mode`). */
export const MISCH_CSS = {
    normal: 'normal',
    multiply: 'multiply',
    screen: 'screen',
    softlight: 'soft-light',
};
/** Und wie sie auf einem Knopf heissen. */
export const MISCH_NAMEN = {
    normal: 'Normal',
    multiply: 'Multiplizieren',
    screen: 'Aufhellen',
    softlight: 'Weiches Licht',
};
export const Track = z.object({
    id: ID,
    kind: z.enum(['video', 'audio']),
    /** Nur für Bildspuren über der Basis; auf der Basis gibt es nichts darunter. */
    mischmodus: Mischmodus.default('normal'),
    name: z.string().max(80).default(''),
    muted: z.boolean().default(false),
    hidden: z.boolean().default(false),
    locked: z.boolean().default(false),
    /** Level for the whole track, multiplied with each clip's own gain. */
    gain: z.number().min(0).max(2).default(1),
    /** A music bed: drops to `duckLevel` whenever speech elsewhere is audible. */
    duck: z.boolean().default(false),
    duckLevel: z.number().min(0.05).max(1).default(0.25),
    clips: z.array(Clip).default([]),
});
export const SequenceSettings = z.object({
    width: z.number().int().min(64).max(4096).default(1080),
    height: z.number().int().min(64).max(4096).default(1920),
    fps: z.number().min(1).max(120).default(30),
    sampleRate: z.number().int().default(48000),
    background: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#000000'),
    /**
     * Auf welche Lautheit der fertige Film gebracht wird (S20), in LUFS --
     * `null` heisst: so lassen, wie es gemischt ist.
     *
     * Eine Einstellung des Films und nicht des Exports: derselbe Schnitt soll
     * nicht heute auf −23 und morgen auf −14 herauskommen, je nachdem, wer den
     * Knopf drückt.
     */
    zielLautheit: z.number().min(-40).max(-5).nullable().default(null),
    /**
     * Ob ein neuer Exportbereich an seinen Clips hängt oder fest auf der Zeit
     * steht (7.166, als Einstellung seit dem 30. September: „Dann noch
     * Einstellungen ob der Bereichmarker wo anheftet“). Die Wahl im Blatt
     * eines Bereichs setzt sie mit -- der nächste fängt so an wie der letzte.
     */
    bereicheAnheften: z.boolean().default(true),
    /**
     * **Magnetische Zeitleiste** (7.201): die Hauptspur ohne Lücken, und was
     * darüber liegt, wandert mit ihrem Clip (`magnetZug`). Ohne Angabe aus --
     * so bleiben Schnitte von vorher, wie sie sind; neue Projekte aus der App
     * fangen mit an.
     */
    magnetisch: z.boolean().optional(),
    /**
     * **Neue Clips gleich laut** (7.210; Sascha, 4. Oktober: „Auto
     * Normalisierung“). Ein Clip mit Ton, der neu auf die Zeitleiste kommt,
     * bekommt den Pegel, den „Normalisieren“ ihm geben würde. Ohne Angabe an;
     * `false` schaltet es für das Projekt ab.
     */
    autoNormalisieren: z.boolean().optional(),
});
export const Subtitles = z.object({
    enabled: z.boolean().default(false),
    /** Which transcript to burn in. */
    transcriptId: ID.nullable().default(null),
    style: TextStyle.default({}),
    /** Vertical position, fraction of canvas height. */
    y: z.number().min(0).max(1).default(0.82),
    /** Maximum characters per rendered subtitle line. */
    maxLineLength: z.number().int().min(10).max(80).default(38),
    /**
     * Ob neben dem fertigen Film eine `.srt` liegt (S21) -- „der Weg hinaus“.
     *
     * Getrennt von `enabled`, und beides zusammen ist nicht dasselbe: Eingebrannt
     * bekommt man nicht wieder weg, und YouTube, Instagram und jeder Sender
     * wollen die Untertitel als eigene Datei, die man ab- und anschalten kann.
     * Wer beides ankreuzt, bekommt beides; wer nur dies hier ankreuzt, bekommt
     * einen sauberen Film mit einer Datei daneben.
     *
     * Voreingestellt aus: was bisher hinausging, soll morgen nicht plötzlich zu
     * zweit hinausgehen.
     */
    alsDatei: z.boolean().default(false),
    /**
     * Schlagwörter hervorheben (4. November): Farbe und Grösse der Wörter, die
     * eine Abschrift als betont führt (`Segment.betont`). Ohne betonte Wörter
     * ändert sich nichts.
     */
    betonFarbe: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#ffd400'),
    betonGroesse: z.number().min(1).max(2).default(1.2),
});
/**
 * Eine benannte Stelle in der Zeitleiste (S5).
 *
 * Sascha: „Stellen in der Zeitleiste benennen, und aus einem Bereich heraus
 * exportieren.“ Eine Marke hängt an einem Zeitpunkt und nicht an einem Clip:
 * „hier kommt die Bauchbinde hin“ gilt für die Stelle, auch wenn der Clip
 * darunter ausgetauscht wird. Dieselbe Entscheidung wie bei den Hinweisen
 * (7.8a), und aus demselben Grund.
 */
export const Marke = z.object({
    id: ID,
    /** Sekunden auf der Zeitleiste. */
    at: z.number().min(0),
    label: z.string().max(80).default(''),
});
/**
 * Ein benannter Bereich, aus dem ein eigener Film wird (S33).
 *
 * Sascha, 17. September: „Wir brauchen exportbereiche. Wie outtakes, Storys
 * usw. So kann man auch mehrere Medien in einen exportbereich kopieren.“
 *
 * **Abgegrenzt vom Bereich aus S5**, und der Unterschied ist der ganze Punkt:
 * jener ist *einer*, namenlos, und steht nur in dieser Sitzung -- die Absicht
 * für den nächsten Export. Dieser hier ist *einer von mehreren*, hat einen
 * Namen und **bleibt am Schnitt**. „Outtakes“ ist keine Absicht für den
 * nächsten Knopfdruck, sondern eine Eigenschaft des Films.
 *
 * Und deshalb ein Bereich auf der Zeitleiste und keine Sammlung von Medien:
 * was in „Storys“ gehört, ist ein *geschnittenes Stück* -- mit seinen
 * Blenden, seiner Bauchbinde und seinem Ton. Eine Liste von Dateien daneben
 * wäre eine zweite Wahrheit, die niemand pflegt. „Mehrere Medien in einen
 * Exportbereich kopieren“ heisst hier: sie liegen darin.
 */
/**
 * Die Farben, in denen ein Bereich stehen kann (S42).
 *
 * Sascha, 22. September: „Und Farbe“. Eine feste, kleine Auswahl und kein
 * freier Farbwert: sechs Farben lassen sich auf einem Telefon mit dem Daumen
 * treffen, ein Farbrad nicht -- und sie sehen im dunklen wie im hellen Thema
 * gleich gut aus, weil sie ausgesucht und nicht getippt sind.
 */
export const BEREICH_FARBEN = ['blau', 'violett', 'tuerkis', 'gruen', 'gelb', 'rot'];
/** Der Farbwert je Farbe -- an *einer* Stelle, für Leiste, Blatt und Prüfung. */
export const BEREICH_FARBWERTE = {
    blau: '#4aa3ff',
    violett: '#a878ff',
    tuerkis: '#2fd0c5',
    gruen: '#57c96a',
    gelb: '#e8b23a',
    rot: '#f06a6a',
};
/**
 * Namen, die die App vorschlägt -- **mit einer Länge dahinter** (S42).
 *
 * Sascha, 22. September: „Vorschläge wie Reel, Story, outtakes“. Genau seine
 * drei, dazu „Teaser“ -- ein Tipp legt den Bereich an, und niemand muss auf
 * einem Telefon einen Namen tippen, um anzufangen.
 *
 * **Warum eine Länge und nicht immer bis zum Ende:** das sind Formate, keine
 * Etiketten. Eine Story ist eine Karte von fünfzehn Sekunden, ein Reel fängt
 * gut bei dreissig an, ein Teaser ist zehn. Wer „Story“ antippt, bekommt
 * damit ein Stück, das er *sieht* und dessen rechte Kante neben dem
 * Abspielkopf liegt -- statt eines Bandes bis ans Ende des Films, dessen
 * Kante man erst suchen muss. „Outtakes“ ist die Ausnahme und geht bis zum
 * Ende: was übrig ist, ist übrig.
 *
 * Länger als der Film wird nichts: die Länge ist ein Vorschlag, das Ende ist
 * eine Grenze.
 */
/**
 * **Die Art eines Bereichs** (7.196; Sascha, 3. Oktober: „name und art des
 * bereichs“). Bis dahin war ein Vorschlag Name *und* Farbe zugleich -- ein
 * eigener Name wie „Interview Müller“ war keine Story mehr. Die Art sagt, was
 * der Bereich ist, und bringt die Farbe mit; der Name bleibt frei.
 */
export const BEREICH_ARTEN = ['video', 'story', 'outtakes', 'reel', 'teaser'];
export const BEREICH_VORSCHLAEGE = [
    // **Jeder Vorschlag mit seiner Farbe** (Sascha, 30. September: „Vorschläge
    // wie Story, Video, outtakes mit Farben. Video Blau wie jetzt, Storys Rot,
    // Outtakes gelb“). Die Farbe sagt damit, *was* ein Bereich ist -- alle
    // Storys sind rot, gleich wie viele es gibt. „Video“ ist der ganze Film ab
    // dem Kopf, also bis zum Ende wie „Outtakes“.
    { name: 'Video', sekunden: null, farbe: 'blau', art: 'video' },
    { name: 'Story', sekunden: 15, farbe: 'rot', art: 'story' },
    { name: 'Outtakes', sekunden: null, farbe: 'gelb', art: 'outtakes' },
    { name: 'Reel', sekunden: 30, farbe: 'violett', art: 'reel' },
    { name: 'Teaser', sekunden: 10, farbe: 'tuerkis', art: 'teaser' },
];
/** Die Farbe, die zu einem Namen gehört -- wenn er einer der Vorschläge ist. */
export function farbeZumNamen(name) {
    const grund = name.trim().replace(/\s+\d+$/, '').toLocaleLowerCase('de');
    const treffer = BEREICH_VORSCHLAEGE.find((v) => v.name.toLocaleLowerCase('de') === grund || `${v.name.toLocaleLowerCase('de')}s` === grund);
    return treffer?.farbe ?? null;
}
/** Die Art, die zu einem Namen gehört -- wenn er einer der Vorschläge ist. */
export function artZumNamen(name) {
    const grund = name.trim().replace(/\s+\d+$/, '').toLocaleLowerCase('de');
    const treffer = BEREICH_VORSCHLAEGE.find((v) => v.name.toLocaleLowerCase('de') === grund || `${v.name.toLocaleLowerCase('de')}s` === grund);
    return treffer?.art ?? null;
}
/**
 * Die Art eines Bereichs: gesetzt, oder aus dem Namen. Ein Bereich von vor dem
 * 3. Oktober, der „Story 2“ heisst, ist eine Story, ohne dass ihn jemand
 * anfasst; `null` heisst ausdrücklich „keine Art“.
 */
export function artVon(b) {
    return b.art !== undefined ? b.art : artZumNamen(b.name);
}
/** Ein Name, den noch kein anderer Bereich trägt: „Story“, „Story 2“, … */
export function freierBereichsname(vorhandene, name, ausser) {
    const sauber = name.trim().slice(0, 60) || 'Bereich';
    let frei = sauber;
    for (let n = 2; vorhandene.some((b) => b.id !== ausser && b.name === frei); n++)
        frei = `${sauber} ${n}`.slice(0, 60);
    return frei;
}
export const Exportbereich = z.object({
    id: ID,
    name: z.string().min(1).max(60),
    von: z.number().min(0),
    bis: z.number().min(0),
    /** Seine Farbe (S42). Was vor dem 22. September angelegt wurde, ist blau. */
    farbe: z.enum(BEREICH_FARBEN).default('blau'),
    /**
     * **Angeheftet** (7.166): der Bereich hält sich an seinen Inhalt statt an
     * die Uhr. Sascha, 24. September: „irgendwie anheften oderso. Damit wenn
     * ich was verschiebe, ich es nicht aus dem Bereich heraus schiebe“.
     * Woran die Kanten hängen, rechnet `bereichAnheften`; nachgezogen wird in
     * `applyOp` (siehe `bereiche.ts`).
     * Optional, damit ein Bereich von vor dem 24. September bleibt, was er war.
     */
    angeheftet: z.boolean().optional(),
    /** Was der Bereich ist (7.196). Fehlt sie, gilt die Art des Namens (`artVon`). */
    art: z.enum(BEREICH_ARTEN).nullable().optional(),
    /**
     * **Woran er hängt** (7.196; Sascha, 3. Oktober: „bereich anpinnen (wandert
     * mit bei elementXY)“). Ohne Angabe an der untersten Bildspur wie seit
     * 7.166; mit `spur` an den Clips dieser Spur, mit `clip` an genau diesem
     * Clip -- dann wandert der ganze Bereich mit ihm. Gibt es Clip oder Spur
     * nicht mehr, gilt wieder die unterste Bildspur.
     */
    heftenAn: z.object({ spur: ID.optional(), clip: ID.optional() }).optional(),
    /** Woran die vordere Kante hängt: ein Bild in einem Clip, oder ein Abstand zu ihm. */
    vonAnker: z.object({ clipId: ID, quelle: z.number().optional(), abstand: z.number().optional() }).optional(),
    /** Und die hintere. */
    bisAnker: z.object({ clipId: ID, quelle: z.number().optional(), abstand: z.number().optional() }).optional(),
});
/**
 * Ein neuer Bereich: Name frei gemacht, Farbe der Reihe nach (S42).
 *
 * **Der Name wird nicht abgewiesen, sondern weitergezählt.** Wer zweimal auf
 * „Story“ tippt, will zwei Storys -- die zweite heisst „Story 2“. Eine
 * Fehlermeldung an dieser Stelle wäre eine Bremse ohne Grund.
 *
 * **Die Farbe kommt aus der Reihe** und nicht per Zufall: die nächste, die
 * noch niemand hat, sonst die nächste überhaupt. Zwei Bereiche nebeneinander
 * sind damit verschieden, ohne dass jemand wählen muss.
 */
export function neuerBereich(vorhandene, name, von, bis, id = `eb-${Math.random().toString(36).slice(2, 10)}`) {
    const frei = freierBereichsname(vorhandene, name);
    // Ein Vorschlag bringt seine Farbe mit (30. September); ein eigener Name
    // bekommt die nächste freie.
    const belegt = new Set(vorhandene.map((b) => b.farbe));
    const farbe = farbeZumNamen(name) ??
        BEREICH_FARBEN.find((f) => !belegt.has(f)) ??
        BEREICH_FARBEN[vorhandene.length % BEREICH_FARBEN.length];
    return { id, name: frei, von: Math.max(0, Math.min(von, bis)), bis: Math.max(von, bis), farbe };
}
export const Timeline = z.object({
    version: z.literal(1).default(1),
    settings: SequenceSettings.default({}),
    tracks: z.array(Track).default([]),
    /** Benannte Stellen (S5). */
    marken: z.array(Marke).max(200).default([]),
    /** Benannte Bereiche, aus denen eigene Filme werden (S33). */
    exportbereiche: z.array(Exportbereich).max(50).default([]),
    subtitles: Subtitles.default({}),
    /** Brand kit id resolved by the render worker; null = project default. */
    brandKitId: ID.nullable().default(null),
});
export const Asset = z.object({
    id: ID,
    projectId: ID,
    boxFileId: z.string(),
    name: z.string(),
    kind: z.enum(['video', 'audio', 'image']),
    durationSec: z.number().min(0),
    width: z.number().int().min(0),
    height: z.number().int().min(0),
    fps: z.number().min(0),
    hasAudio: z.boolean(),
    status: z.enum(['pending', 'ingesting', 'ready', 'failed']),
});
export function emptyTimeline(settings) {
    return Timeline.parse({
        settings: settings ?? {},
        tracks: [
            { id: 'V1', kind: 'video', name: 'Video 1', clips: [] },
            { id: 'V2', kind: 'video', name: 'Overlay', clips: [] },
            { id: 'A1', kind: 'audio', name: 'Audio 1', clips: [] },
        ],
    });
}
/** End of the last clip on any track. */
export function timelineDuration(t) {
    let end = 0;
    for (const track of t.tracks) {
        for (const clip of track.clips) {
            end = Math.max(end, clip.start + clip.duration);
        }
    }
    return end;
}
export function baseVideoTrack(t) {
    return t.tracks.find((tr) => tr.kind === 'video');
}
export function findClip(t, clipId) {
    for (const track of t.tracks) {
        const clip = track.clips.find((c) => c.id === clipId);
        if (clip)
            return { track, clip };
    }
    return undefined;
}
/** Clips sorted by start time; the renderer and the UI both rely on this order. */
/**
 * Every point where something starts or ends, on the frame grid and without
 * duplicates, with the start of the sequence always among them. These are the
 * places worth jumping to: on a phone, finding a cut by dragging the playhead
 * with a fingertip is hopeless.
 *
 * Hidden tracks are left out -- jumping to a cut nobody can see would look
 * like the button is broken.
 */
export function cutTimes(t) {
    const fps = t.settings.fps;
    const points = new Set([0]);
    for (const track of t.tracks) {
        if (track.hidden)
            continue;
        for (const clip of track.clips) {
            points.add(Math.round(clip.start * fps));
            points.add(Math.round((clip.start + clip.duration) * fps));
        }
    }
    return [...points].sort((a, b) => a - b).map((frame) => frame / fps);
}
/**
 * The next cut strictly after (or before) `from`. Returns null when there is
 * none, so the caller can leave the playhead where it is instead of jumping
 * to an end that is not a cut.
 */
export function nextCut(t, from, direction) {
    const fps = t.settings.fps;
    const here = Math.round(from * fps);
    const points = cutTimes(t).map((sec) => Math.round(sec * fps));
    const candidates = direction === 1 ? points.filter((f) => f > here) : points.filter((f) => f < here).reverse();
    const found = candidates[0];
    return found === undefined ? null : found / fps;
}
export function sortedClips(track) {
    return [...track.clips].sort((a, b) => a.start - b.start || a.id.localeCompare(b.id));
}
/**
 * Where a dragged clip should actually land.
 *
 * Either edge sticks: the head to a cut, or the tail to the head of the next
 * clip. Sticking with the tail is what puts two clips exactly one behind the
 * other -- to the frame, with no gap and no overlap -- which is the whole
 * point of dragging one there. The closest of the two wins, and nothing is
 * ever pushed before zero.
 */
/**
 * Wohin ein Clip auf einer Spur wirklich darf.
 *
 * Auf **einer** Spur liegt nichts übereinander — dafür gibt es Spuren.
 * (Sascha, 5. September: „Warum kann ich auf einer Ebene Dinge übereinander
 * ziehen? Dafür haben wir andere Ebenen.“) Landet ein Clip auf einem anderen,
 * weicht er in die nächste Lücke aus, die ihn fasst.
 *
 * **Ausweichen, nicht verschickt werden.** Die erste Fassung nahm jede Lücke,
 * auch eine hinter einem zehn Minuten langen Nachbarn — der Clip war dann weg,
 * und mit ihm die Stelle, an die man ihn ziehen wollte (Saschas Fund vom
 * 6. September: „dann ist das Video einfach verschwunden“). Deshalb der
 * `spielraum`: weiter als seine eigene Länge rutscht ein Clip nicht. Passt
 * nichts in diesen Rahmen, gibt es hier **keinen Platz** — `null` —, und der
 * Aufrufer lässt ihn, wo er war, statt ihn irgendwohin zu schicken.
 *
 * `belegt` sind die übrigen Clips der Zielspur — der bewegte gehört nicht
 * dazu, sonst wiche er vor sich selbst zurück.
 */
export function freieStelle(gewuenscht, duration, belegt, spielraum = Math.max(duration, 1)) {
    const start = Math.max(0, gewuenscht);
    if (duration <= 0)
        return start;
    const andere = [...belegt].sort((a, b) => a.start - b.start);
    const stoert = andere.some((c) => start < c.start + c.duration && start + duration > c.start);
    if (!stoert)
        return start;
    // Die Lücken: vor dem ersten Clip, zwischen je zweien, hinter dem letzten.
    const luecken = [];
    let rand = 0;
    for (const c of andere) {
        if (c.start > rand)
            luecken.push({ von: rand, bis: c.start });
        rand = Math.max(rand, c.start + c.duration);
    }
    luecken.push({ von: rand, bis: Number.POSITIVE_INFINITY });
    let beste = null;
    let abstand = Number.POSITIVE_INFINITY;
    for (const l of luecken) {
        if (l.bis - l.von < duration)
            continue;
        // In der Lücke so nah an der gewünschten Stelle wie möglich. Die letzte
        // Lücke ist offen -- dort begrenzt nur ihr Anfang.
        const obere = l.bis === Number.POSITIVE_INFINITY ? Number.POSITIVE_INFINITY : l.bis - duration;
        const hier = Math.min(Math.max(start, l.von), obere);
        const d = Math.abs(hier - start);
        if (d <= spielraum && d < abstand) {
            abstand = d;
            beste = Math.max(0, hier);
        }
    }
    return beste;
}
export function snapClipStart(start, duration, points, tolerance) {
    let best = start;
    let bestDelta = tolerance;
    for (const point of points) {
        const asHead = Math.abs(point - start);
        if (asHead < bestDelta) {
            best = point;
            bestDelta = asHead;
        }
        const asTail = Math.abs(point - (start + duration));
        if (asTail < bestDelta && point - duration >= 0) {
            best = point - duration;
            bestDelta = asTail;
        }
    }
    return Math.max(0, best);
}
/**
 * Der Massstab, mit dem eine Ebene den Rahmen ausfuellt (7.14).
 *
 * `scale` heisst in Vorschau und Export dasselbe: die Breite der Ebene,
 * gemessen in Rahmenbreiten -- `scale: 1` ist genau so breit wie das Bild,
 * die Hoehe folgt dem Seitenverhaeltnis. Damit nichts frei bleibt, muss die
 * so entstehende Hoehe mindestens die Rahmenhoehe erreichen.
 *
 * Fuellen, nicht einpassen: ein Standbild in einem Hochkant-Projekt mit
 * schwarzen Balken oben und unten ist keine Loesung, sondern das Problem. Was
 * ueber den Rand steht, wird beschnitten -- und wer es anders will, zieht die
 * Ebene von Hand.
 */
export function fuellFaktor(medium, rahmen) {
    if (medium.width <= 0 || medium.height <= 0 || rahmen.width <= 0 || rahmen.height <= 0)
        return 1;
    const seiten = medium.width / medium.height;
    // Breit genug ist schon `1`; hoch genug wird es erst bei diesem Wert.
    const fuerDieHoehe = (rahmen.height * seiten) / rahmen.width;
    return Math.round(Math.max(1, fuerDieHoehe) * 1000) / 1000;
}
//# sourceMappingURL=timeline.js.map